import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonV3 } from 'components/ComponentV2';
import { OrganizationOpportunityCard } from 'components/ComponentV2/OrganizationOpportunityCard';
import { BulletList, LinkList } from 'components/ComponentV2/Lists';
import { CompanyActions } from 'components/company-page';
import { OrganizationEvents, OrganizationSectionGroup } from 'components/organization-page';
import Link from 'components/Link';
import { useResponsive } from 'hooks/useResponsive';
import { PER_PAGE_COUNT } from 'hooks/useCompany/useCompanyInternal';
import { OrganizationTypeEnum } from 'lib/models/organization';
import { parseArrayResponse } from 'lib/utils/parser';
import { useCompany } from 'lib/providers/CompanyProvider';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useOrganizationEvents } from 'lib/providers/OrganizationEventsProvider';
import { isDataAvailable } from 'utils/common.utils';

enum TabPanelIds {
  JOBS = 'jobs',
  EVENTS = 'events',
  MISSION = 'mission',
  CULTURE_VALUES = 'culture-values',
  BENEFITS = 'benefits',
  NEWS = 'news',
}

import styles from './CompanySectionGroup.module.scss';

export const CompanySectionGroup = () => {
  const [t] = useTranslation('company');
  const screens = useResponsive();
  const { company, publishedJobs, publishedJobsTotalCount, publishedInternshipsTotalCount, showBanner } = useCompany();
  const { description, culture_stats, benefit_stats, insights: insightsResponse } = company;
  const cultureStats = parseArrayResponse(culture_stats).filter((item) => isDataAvailable(item.title));
  const benefitStats = parseArrayResponse(benefit_stats).filter((item) => isDataAvailable(item.title));
  const insights = parseArrayResponse(insightsResponse).filter((item) => isDataAvailable(item.title) && isDataAvailable(item.url));
  const companyJobsPage = `${PAGE_ROUTES.JOBS_SEARCH}&posted_by_company_ids=${company.id}`;
  const companyInternshipsPage = `${PAGE_ROUTES.INTERNSHIPS_SEARCH}&posted_by_company_ids=${company.id}`;

  const { showEventsTab } = useOrganizationEvents();
  const showJobsTab = publishedJobs.length > 0;
  const showMissionTab = !!description?.trim().length;
  const showCultureValuesTab = cultureStats.length > 0;
  const showBenefitsTab = benefitStats.length > 0;
  const showNewsTab = !screens.sm && insights.length > 0;

  const tabList = useMemo(() => {
    return [
      {
        id: TabPanelIds.JOBS,
        hash: `#${TabPanelIds.JOBS}`,
        title: t('navigation.jobs'),
        badgeCount: publishedJobsTotalCount,
        hidden: !showJobsTab,
      },
      {
        id: TabPanelIds.EVENTS,
        hash: `#${TabPanelIds.EVENTS}`,
        title: t('navigation.events'),
        hidden: !showEventsTab,
      },
      {
        id: TabPanelIds.MISSION,
        hash: `#${TabPanelIds.MISSION}`,
        title: t('navigation.mission'),
        hidden: !showMissionTab,
      },
      {
        id: TabPanelIds.CULTURE_VALUES,
        hash: `#${TabPanelIds.CULTURE_VALUES}`,
        title: t('navigation.culture-values'),
        hidden: !showCultureValuesTab,
      },
      {
        id: TabPanelIds.BENEFITS,
        hash: `#${TabPanelIds.BENEFITS}`,
        title: t('navigation.benefits'),
        hidden: !showBenefitsTab,
      },
      {
        id: TabPanelIds.NEWS,
        hash: `#${TabPanelIds.NEWS}`,
        title: t('navigation.news'),
        hidden: !showNewsTab,
      },
    ];
  }, [showJobsTab, showEventsTab, showMissionTab, showCultureValuesTab, showBenefitsTab, showNewsTab, publishedJobsTotalCount]);

  return (
    <OrganizationSectionGroup
      tabList={tabList}
      organizationActions={<CompanyActions buttonSize="small" />}
      organizationType={OrganizationTypeEnum.COMPANY}
      showBanner={showBanner}
    >
      {showJobsTab && (
        <>
          {publishedJobs.map((job) => (
            <OrganizationOpportunityCard key={job.id} opportunity={job} className={styles['opportunity-card']} showOrganization={false} />
          ))}
          {(publishedJobsTotalCount > PER_PAGE_COUNT || publishedInternshipsTotalCount > 0) && (
            <div className={styles.footer}>
              {publishedJobsTotalCount > PER_PAGE_COUNT && (
                <Link href={companyJobsPage} className={styles['view-all']}>
                  <ButtonV3 startIcon="icon_search" color="silent" borderless isFullWidth={screens.sm}>
                    {t('jobs.cta')}
                  </ButtonV3>
                </Link>
              )}
              {publishedInternshipsTotalCount > 0 && (
                <Link href={companyInternshipsPage} className={styles['view-all']}>
                  <ButtonV3 startIcon="icon_search" color="silent" borderless isFullWidth={screens.sm}>
                    {t('internships.cta')}
                  </ButtonV3>
                </Link>
              )}
            </div>
          )}
        </>
      )}
      {showEventsTab && <OrganizationEvents />}
      {showMissionTab && <p className={styles.mission}>{description}</p>}
      {showCultureValuesTab && <BulletList title={t('navigation.culture-values')} items={cultureStats} size="large" hideTitle />}
      {showBenefitsTab && <BulletList title={t('navigation.benefits')} items={benefitStats} size="large" listStyleType="tick" hideTitle />}
      {showNewsTab && <LinkList title={t('navigation.news')} items={insights} size="large" hideTitle />}
    </OrganizationSectionGroup>
  );
};

CompanySectionGroup.displayName = 'CompanySectionGroup';
